/**
 * Line Item Section - Business Value Calculator
 * Sprint 7: Day 2
 *
 * Generic reusable component for 6 line item sections:
 * - Training
 * - Migration
 * - Compliance
 * - Vendor
 * - Risk
 * - Disruption
 *
 * Features:
 * - Add/remove rows dynamically
 * - Auto-calculation with real-time updates
 * - Category field with predefined options
 * - Description field for detailed notes
 */

import React from 'react';

export interface LineItem {
  id: string;
  category: string;
  description: string;
  unitCost: number;
  quantity: number;
  totalCost: number;
}

interface LineItemSectionProps {
  sectionName: string;
  items: LineItem[];
  categories: string[];
  helpText: string;
  onItemUpdate: (itemId: string, field: keyof LineItem, value: string | number) => void;
  onItemAdd: () => void;
  onItemRemove: (itemId: string) => void;
}

export const LineItemSection: React.FC<LineItemSectionProps> = ({
  sectionName,
  items,
  categories,
  helpText,
  onItemUpdate,
  onItemAdd,
  onItemRemove
}) => {
  // Handle input change with validation
  const handleInputChange = (
    itemId: string,
    field: keyof LineItem,
    value: string
  ) => {
    if (field === 'unitCost' || field === 'quantity') {
      const numericValue = parseFloat(value) || 0;
      if (numericValue < 0) return; // Prevent negative values
      onItemUpdate(itemId, field, numericValue);
    } else {
      onItemUpdate(itemId, field, value);
    }
  };

  return (
    <div className="line-item-section">
      <div className="section-help-text">
        <strong>{sectionName}:</strong> {helpText}
      </div>

      <table className="line-item-table">
        <thead>
          <tr>
            <th style={{ textAlign: 'left', width: '25%' }}>Category</th>
            <th style={{ textAlign: 'left', width: '35%' }}>Description</th>
            <th style={{ textAlign: 'right', width: '15%' }}>Unit Cost</th>
            <th style={{ textAlign: 'right', width: '10%' }}>Quantity</th>
            <th style={{ textAlign: 'right', width: '15%' }}>Total</th>
            <th style={{ width: '40px' }}></th>
          </tr>
        </thead>
        <tbody>
          {items.map((item) => (
            <tr key={item.id}>
              <td>
                <select
                  value={item.category}
                  onChange={(e) => handleInputChange(item.id, 'category', e.target.value)}
                  aria-label={`${sectionName} category`}
                  className="line-item-select"
                >
                  <option value="">Select category...</option>
                  {categories.map((category) => (
                    <option key={category} value={category}>
                      {category}
                    </option>
                  ))}
                </select>
              </td>
              <td>
                <input
                  type="text"
                  value={item.description}
                  onChange={(e) => handleInputChange(item.id, 'description', e.target.value)}
                  placeholder="Enter description..."
                  aria-label={`${sectionName} description`}
                  className="line-item-input"
                />
              </td>
              <td>
                <input
                  type="number"
                  min="0"
                  step="100"
                  value={item.unitCost}
                  onChange={(e) => handleInputChange(item.id, 'unitCost', e.target.value)}
                  aria-label={`${sectionName} unit cost`}
                  className="line-item-input"
                  style={{ textAlign: 'right' }}
                />
              </td>
              <td>
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={item.quantity}
                  onChange={(e) => handleInputChange(item.id, 'quantity', e.target.value)}
                  aria-label={`${sectionName} quantity`}
                  className="line-item-input"
                  style={{ textAlign: 'right' }}
                />
              </td>
              <td style={{ textAlign: 'right', fontWeight: 600, color: '#0094ab' }}>
                ${item.totalCost.toLocaleString()}
              </td>
              <td>
                <button
                  type="button"
                  onClick={() => onItemRemove(item.id)}
                  aria-label={`Remove ${item.category || 'line item'}`}
                  className="line-item-remove-btn"
                  disabled={items.length === 1}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <td colSpan={6} style={{ padding: '1rem 0' }}>
              <button
                type="button"
                onClick={onItemAdd}
                className="line-item-add-btn"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="12" y1="5" x2="12" y2="19" />
                  <line x1="5" y1="12" x2="19" y2="12" />
                </svg>
                Add Line Item
              </button>
            </td>
          </tr>
          <tr style={{ borderTop: '2px solid #2d3748' }}>
            <td colSpan={4} style={{ textAlign: 'right', fontWeight: 700, padding: '1rem' }}>
              Total {sectionName} Costs:
            </td>
            <td style={{ textAlign: 'right', fontWeight: 700, fontSize: '1.125rem', color: '#0094ab', padding: '1rem' }}>
              ${items.reduce((sum, item) => sum + item.totalCost, 0).toLocaleString()}
            </td>
            <td></td>
          </tr>
        </tfoot>
      </table>

      <style>{`
        .section-help-text {
          background: rgba(0, 148, 171, 0.05);
          border-left: 4px solid #0094ab;
          border-radius: 8px;
          padding: 1rem;
          margin-bottom: 1.5rem;
          font-size: 0.875rem;
          color: #4a5568;
          line-height: 1.6;
        }

        .line-item-table {
          width: 100%;
          border-collapse: collapse;
          margin-top: 1rem;
        }

        .line-item-table thead th {
          padding: 0.75rem 1rem;
          font-size: 0.75rem;
          font-weight: 700;
          color: #718096;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          background: #f7fafc;
          border-bottom: 2px solid #e2e8f0;
        }

        .line-item-table tbody td {
          padding: 0.75rem 1rem;
          border-bottom: 1px solid #e2e8f0;
          vertical-align: middle;
        }

        .line-item-table tbody tr:hover {
          background: #f7fafc;
        }

        .line-item-input,
        .line-item-select {
          width: 100%;
          padding: 0.5rem 0.75rem;
          font-size: 0.875rem;
          border: 1px solid #e2e8f0;
          border-radius: 6px;
          font-weight: 600;
          transition: all 0.2s ease;
          background: #ffffff;
        }

        .line-item-input:focus,
        .line-item-select:focus {
          outline: none;
          border-color: #0094ab;
          box-shadow: 0 0 0 3px rgba(0, 148, 171, 0.1);
        }

        .line-item-input:hover,
        .line-item-select:hover {
          border-color: #cbd5e0;
        }

        .line-item-select {
          cursor: pointer;
        }

        .line-item-add-btn {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.625rem 1rem;
          font-size: 0.875rem;
          font-weight: 600;
          color: #0094ab;
          background: transparent;
          border: 2px dashed #cbd5e0;
          border-radius: 8px;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .line-item-add-btn:hover {
          border-color: #0094ab;
          background: rgba(0, 148, 171, 0.05);
        }

        .line-item-add-btn:active {
          transform: scale(0.98);
        }

        .line-item-remove-btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 32px;
          height: 32px;
          padding: 0;
          background: transparent;
          border: 1px solid #e2e8f0;
          border-radius: 6px;
          cursor: pointer;
          transition: all 0.2s ease;
          color: #718096;
        }

        .line-item-remove-btn:hover:not(:disabled) {
          background: #fee;
          border-color: #fc8181;
          color: #e53e3e;
        }

        .line-item-remove-btn:disabled {
          opacity: 0.3;
          cursor: not-allowed;
        }

        .line-item-remove-btn:active:not(:disabled) {
          transform: scale(0.95);
        }
      `}</style>
    </div>
  );
};

// Predefined categories for each section type
export const LINE_ITEM_CATEGORIES = {
  training: [
    'Instructor-led Training',
    'E-learning Platform',
    'Training Materials',
    'User Documentation',
    'Certification Programs',
    'Workshop Facilitation'
  ],
  migration: [
    'Data Extraction',
    'ETL Development',
    'Schema Mapping',
    'Validation Testing',
    'Cutover Planning',
    'Rollback Support'
  ],
  compliance: [
    'Regulatory Audit',
    'Policy Documentation',
    'GDPR Assessment',
    'Security Certification',
    'Compliance Training',
    'Audit Support'
  ],
  vendor: [
    'Software License',
    'Professional Services',
    'Support Contract',
    'Maintenance Agreement',
    'Third-party Integration',
    'Consulting Services'
  ],
  risk: [
    'Risk Assessment',
    'Contingency Planning',
    'Insurance Coverage',
    'Security Measures',
    'Backup Solutions',
    'Disaster Recovery'
  ],
  disruption: [
    'Downtime Planning',
    'Communication Campaign',
    'Change Management',
    'User Support',
    'Productivity Loss',
    'Transition Support'
  ]
};

// Create default empty line item
export const createDefaultLineItem = (): LineItem => ({
  id: `item_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
  category: '',
  description: '',
  unitCost: 0,
  quantity: 1,
  totalCost: 0
});

export default LineItemSection;
